import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Building2, Copy, UserPlus, ChevronRight, LogOut, Megaphone, X, Pin, Clock, CalendarDays, MapPin, Plus, Trash2, Bell } from "lucide-react";
import EnvironmentsPanel from "../components/EnvironmentsPanel";
import Brand from "../components/Brand";
import DirectorKPIs from "../components/DirectorKPIs";

const KINDS = [
  { value: "daycare", label: "Daycare" },
  { value: "school", label: "School" },
  { value: "eldercare", label: "Elder care" },
  { value: "hospital", label: "Hospital" },
  { value: "specialneeds", label: "Special-needs" },
  { value: "campus", label: "Smart campus" },
];

export default function Workspace() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "daycare" });
  const [invite, setInvite] = useState(null);
  const [inviteRole, setInviteRole] = useState("caregiver");
  const [inviteBusy, setInviteBusy] = useState(false);

  useEffect(() => {
    api.get("/workspaces").then((r) => {
      setList(r.data);
      if (r.data.length) setSelectedId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    api.get(`/workspaces/${selectedId}`).then((r) => setDetail(r.data));
    setInvite(null);
  }, [selectedId]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateBusy(true);
    try {
      const r = await api.post("/workspaces", {
        name: form.name.trim(),
        kind: form.kind,
      });
      setList([...list, r.data]);
      setSelectedId(r.data.id);
      setCreating(false);
      setForm({ name: "", kind: "daycare" });
      toast("A new center, gently begun.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't create just now.");
    } finally {
      setCreateBusy(false);
    }
  };

  const createInvite = async () => {
    if (!selectedId) return;
    setInviteBusy(true);
    try {
      const r = await api.post(`/workspaces/${selectedId}/invite`, {
        role_to_grant: inviteRole,
      });
      setInvite(r.data);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't create an invite.");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyLink = () => {
    if (!invite) return;
    const url = `${window.location.origin}/accept/workspace/${invite.token}`;
    navigator.clipboard?.writeText(url);
    toast("Link copied. Share it gently.");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Brand size="sm" suffix="Center" dataTestId="brand-workspace" />
          <button
            data-testid="workspace-signout"
            onClick={() => {
              signOut();
              navigate("/");
            }}
            className="text-[#6A6863] hover:text-[#2C2B29] inline-flex items-center gap-2 text-sm transition-colors"
          >
            Sign out
            <LogOut size={14} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-10 pt-6 pb-12 fade-up">
        <span className="eyebrow">A calm at scale</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          The center, <em className="italic text-[#8A9984]">held gently.</em>
        </h1>

        {/* Director KPIs */}
        {detail && selectedId && (
          <DirectorKPIs workspaceId={selectedId} workspaceDetail={detail} />
        )}

        {/* Workspace list + create */}
        <section className="mt-12">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span className="eyebrow">Your spaces</span>
            {user?.role === "admin" && (
              <button
                type="button"
                onClick={() => setCreating((c) => !c)}
                data-testid="workspace-create-toggle"
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                {creating ? "Not now" : "New space"}
                <Building2 size={12} strokeWidth={1.5} />
              </button>
            )}
          </div>

          {list.length === 0 && !creating && (
            <p className="mt-6 font-serif italic text-[#6A6863]">
              No spaces yet. {user?.role === "admin" ? "Create one to begin." : "Wait for an invite."}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {list.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setSelectedId(w.id)}
                data-testid={`workspace-pill-${w.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors duration-300 border ${
                  selectedId === w.id
                    ? "bg-[#9BA995] text-white border-[#9BA995]"
                    : "bg-white border-[#2C2B29]/10 text-[#2C2B29] hover:bg-[#F2EFEA]"
                }`}
              >
                <Building2 size={12} strokeWidth={1.5} />
                {w.name}
              </button>
            ))}
          </div>

          {creating && (
            <form
              onSubmit={submitCreate}
              data-testid="workspace-create-form"
              className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8"
            >
              <label className="eyebrow">A new space</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Maple Grove Daycare"
                data-testid="workspace-create-name"
                className="orbit-input mt-3"
              />
              <div className="mt-7">
                <label className="eyebrow">Kind</label>
                <div className="mt-4 flex flex-wrap gap-2">
                  {KINDS.map((k) => (
                    <button
                      key={k.value}
                      type="button"
                      onClick={() => setForm({ ...form, kind: k.value })}
                      data-testid={`workspace-kind-${k.value}`}
                      className={`rounded-full px-4 py-2 text-xs tracking-widest uppercase transition-colors duration-300 border ${
                        form.kind === k.value
                          ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                          : "bg-transparent text-[#6A6863] border-[#2C2B29]/10 hover:bg-[#FBF9F6]"
                      }`}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={createBusy}
                data-testid="workspace-create-submit"
                className="btn-primary mt-8"
              >
                {createBusy ? "Beginning…" : "Begin gently"}
              </button>
            </form>
          )}
        </section>

        {/* Workspace detail */}
        {detail && (
          <section className="mt-16 space-y-12">
            <header>
              <div className="eyebrow">{detail.workspace.kind}</div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">
                {detail.workspace.name}
              </h2>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden">
              <MemberGroup
                label="Admins"
                members={detail.admins}
                testId="workspace-admins"
              />
              <MemberGroup
                label="Caregivers"
                members={detail.caregivers}
                testId="workspace-caregivers"
              />
              <MemberGroup
                label="Parents"
                members={detail.parents}
                testId="workspace-parents"
              />
            </div>

            {/* Children */}
            <div>
              <span className="eyebrow">Children in this space</span>
              <ol className="mt-6 space-y-px bg-[#2C2B29]/8 rounded-3xl overflow-hidden border hairline">
                {detail.children.length === 0 && (
                  <li className="bg-[#FBF9F6] p-7 font-serif italic text-[#6A6863]">
                    No children assigned to this space yet.
                  </li>
                )}
                {detail.children.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`workspace-child-${c.id}`}
                    className="bg-[#FBF9F6] p-6 sm:p-7 flex items-center gap-4"
                  >
                    <span
                      className="h-12 w-12 rounded-full bg-cover bg-center shrink-0"
                      style={{
                        backgroundImage: c.portrait_url
                          ? `url(${c.portrait_url})`
                          : undefined,
                        backgroundColor: "#E6BFA6",
                      }}
                    />
                    <div className="flex-1">
                      <div className="font-serif text-xl">{c.name}</div>
                      <div className="text-xs text-[#6A6863]">
                        {c.age} · {c.current_state}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Smart Environments OS */}
            <EnvironmentsPanel
              workspaceId={detail.workspace.id}
              isAdmin={
                user?.role === "admin" &&
                detail.admins.some((m) => m.id === user.id)
              }
            />

            {/* Today's plan editor + live pickup alerts (admin) */}
            {user?.role === "admin" &&
              detail.admins.some((m) => m.id === user.id) && (
                <>
                  <PickupAlertsAdmin workspaceId={detail.workspace.id} />
                  <TodayPlanAdmin workspaceId={detail.workspace.id} />
                  <EventsAdmin workspaceId={detail.workspace.id} />
                </>
              )}

            {/* Announcements (admin-managed) */}
            {user?.role === "admin" &&
              detail.admins.some((m) => m.id === user.id) && (
                <AnnouncementsAdmin workspaceId={detail.workspace.id} />
              )}

            {/* Invite */}
            {user?.role === "admin" &&
              detail.admins.some((m) => m.id === user.id) && (
                <div>
                  <span className="eyebrow">Invite someone</span>
                  <div className="mt-6 rounded-3xl border hairline bg-white p-6 sm:p-8">
                    <div className="flex flex-wrap gap-2 mb-6">
                      {["caregiver", "parent", "admin"].map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setInviteRole(r)}
                          data-testid={`workspace-invite-role-${r}`}
                          className={`rounded-full px-4 py-2 text-xs tracking-widest uppercase transition-colors duration-300 border ${
                            inviteRole === r
                              ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                              : "bg-transparent text-[#6A6863] border-[#2C2B29]/10"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                    {invite ? (
                      <div data-testid="workspace-invite-result">
                        <div className="eyebrow">Invite link · works once</div>
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                          <code className="text-xs font-mono bg-[#FBF9F6] border hairline rounded-full px-4 py-2 text-[#2C2B29] break-all">
                            {window.location.origin}/accept/workspace/{invite.token}
                          </code>
                          <button
                            type="button"
                            onClick={copyLink}
                            data-testid="workspace-invite-copy"
                            className="btn-primary !py-2 !px-4 !text-xs"
                          >
                            Copy
                            <Copy size={12} strokeWidth={1.5} />
                          </button>
                        </div>
                        <p className="mt-3 text-xs text-[#6A6863]">
                          Expires {new Date(invite.expires_at).toLocaleDateString()}.
                        </p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={createInvite}
                        disabled={inviteBusy}
                        data-testid="workspace-invite-create"
                        className="btn-primary"
                      >
                        {inviteBusy ? "A moment…" : `Invite a ${inviteRole}`}
                        <UserPlus size={14} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              )}
          </section>
        )}
      </main>
    </div>
  );
}

function MemberGroup({ label, members, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#FBF9F6] p-7"
    >
      <div className="eyebrow">{label}</div>
      <div className="mt-4 space-y-2">
        {members.length === 0 ? (
          <p className="font-serif italic text-[#6A6863]">—</p>
        ) : (
          members.map((m) => (
            <div key={m.id} className="font-serif text-base text-[#2C2B29]">
              {m.name}
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function AnnouncementsAdmin({ workspaceId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", body: "", pinned: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/announcements`, {
        params: { include_dismissed: true },
      });
      setList(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const post = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/announcements`, {
        title: form.title.trim(),
        body: form.body.trim(),
        pinned: form.pinned,
      });
      setForm({ title: "", body: "", pinned: true });
      await load();
      toast("Announcement shared softly.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't share.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      await load();
      toast("Removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <section data-testid="workspace-announcements">
      <div className="flex items-center gap-2">
        <Megaphone size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Announcements</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        A quiet note to every family.
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
        Use this for operational notes — closures, holiday schedules, gentle reminders.
        Pinned messages appear softly on every parent&rsquo;s home.
      </p>

      <ol
        data-testid="workspace-announcements-list"
        className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {loading && (
          <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
            A quiet moment…
          </li>
        )}
        {!loading && list.length === 0 && (
          <li
            data-testid="workspace-announcements-empty"
            className="bg-[#FBF9F6] p-7 font-serif italic text-[#6A6863]"
          >
            No announcements yet. Write the first one below.
          </li>
        )}
        {list.map((a) => (
          <li
            key={a.id}
            data-testid={`workspace-announcement-${a.id}`}
            className="bg-[#FBF9F6] p-6 flex items-start gap-4"
          >
            <span
              className="mt-1 h-8 w-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: a.pinned ? "#FFB800" : "#F2EFEA" }}
              aria-hidden
            >
              <Pin
                size={14}
                strokeWidth={1.5}
                className={a.pinned ? "text-[#1A1B2E]" : "text-[#6A6863]"}
              />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-[#2C2B29]">{a.title}</div>
              <p className="mt-1 text-sm text-[#6A6863] font-light whitespace-pre-line">
                {a.body}
              </p>
              <p className="mt-2 text-xs text-[#6A6863]">
                {a.pinned ? "pinned · " : ""}from {a.created_by_name}
              </p>
            </div>
            <button
              type="button"
              onClick={() => remove(a.id)}
              data-testid={`workspace-announcement-remove-${a.id}`}
              aria-label="Remove"
              className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ol>

      <form
        onSubmit={post}
        data-testid="workspace-announcement-form"
        className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 space-y-5"
      >
        <div>
          <label className="eyebrow">Title</label>
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Closed Friday for staff training"
            data-testid="workspace-announcement-title"
            className="orbit-input mt-2"
          />
        </div>
        <div>
          <label className="eyebrow">Note</label>
          <textarea
            required
            rows={3}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="We&rsquo;ll be closed all day Friday. Back, gently, on Monday."
            data-testid="workspace-announcement-body"
            className="orbit-input mt-2 resize-none"
          />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-3 text-sm text-[#6A6863]">
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
              data-testid="workspace-announcement-pinned"
              className="h-4 w-4 accent-[#1A1B2E]"
            />
            Pin softly on each family&rsquo;s home
          </label>
          <button
            type="submit"
            disabled={busy || !form.title.trim() || !form.body.trim()}
            data-testid="workspace-announcement-submit"
            className="btn-primary !py-2 !px-5 !text-xs"
          >
            {busy ? "Sharing…" : "Share with the families"}
          </button>
        </div>
      </form>
    </section>
  );
}


/** Today's Plan editor — admin posts the day's gentle schedule. */
function TodayPlanAdmin({ workspaceId }) {
  const [plan, setPlan] = useState({ items: [], updated_at: null, updated_by_name: null });
  const [items, setItems] = useState([{ time: "", label: "" }]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${workspaceId}/today-plan`);
      setPlan(r.data);
      const existing = r.data.items || [];
      setItems(existing.length ? existing : [{ time: "", label: "" }]);
    } catch {
      // quiet
    }
  };

  useEffect(() => {
    if (workspaceId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const save = async (e) => {
    e.preventDefault();
    const cleaned = items.filter((i) => i.time.trim() && i.label.trim());
    if (cleaned.length === 0) {
      toast("Add at least one moment.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/workspaces/${workspaceId}/today-plan`, {
        items: cleaned.map((i) => ({ time: i.time.trim(), label: i.label.trim() })),
      });
      await load();
      toast("Today's plan saved gently.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const addRow = () => setItems([...items, { time: "", label: "" }]);
  const setRow = (i, field, v) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: v };
    setItems(next);
  };
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <section data-testid="workspace-today-plan">
      <div className="flex items-center gap-2">
        <Clock size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Today&rsquo;s plan</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        Gently sketch the day.
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
        A soft sequence parents see on their home: outdoor play, quiet lunch,
        rest. Resets each day.
      </p>

      <form
        onSubmit={save}
        data-testid="workspace-today-plan-form"
        className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 space-y-3"
      >
        {items.map((it, i) => (
          <div
            key={i}
            data-testid={`workspace-today-plan-row-${i}`}
            className="flex items-center gap-3"
          >
            <input
              type="text"
              value={it.time}
              onChange={(e) => setRow(i, "time", e.target.value)}
              placeholder="09:00"
              data-testid={`workspace-today-plan-time-${i}`}
              className="orbit-input w-24 font-mono text-xs"
            />
            <input
              type="text"
              value={it.label}
              onChange={(e) => setRow(i, "label", e.target.value)}
              placeholder="Morning circle"
              data-testid={`workspace-today-plan-label-${i}`}
              className="orbit-input flex-1"
            />
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(i)}
                data-testid={`workspace-today-plan-remove-${i}`}
                aria-label="Remove"
                className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={addRow}
            data-testid="workspace-today-plan-add-row"
            className="btn-ghost !py-1.5 !px-3.5 !text-[10px]"
          >
            Add a moment
            <Plus size={12} strokeWidth={1.5} />
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="workspace-today-plan-save"
            className="btn-primary !py-2 !px-5 !text-xs"
          >
            {busy ? "Saving…" : "Save today's plan"}
          </button>
        </div>
        {plan.updated_by_name && (
          <p className="text-xs text-[#6A6863]">
            Last set by {plan.updated_by_name}.
          </p>
        )}
      </form>
    </section>
  );
}

/** Live pickup alerts panel — staff sees parents who are arriving / running late. */
function PickupAlertsAdmin({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pickup/active");
      const here = (r.data || []).filter((p) => p.workspace_id === workspaceId);
      setItems(here);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId) {
      load();
      const id = setInterval(load, 30 * 1000);
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const resolve = async (id) => {
    try {
      await api.post(`/pickup/resolve/${id}`);
      await load();
      toast("Handoff acknowledged.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't acknowledge.");
    }
  };

  return (
    <section data-testid="workspace-pickup-alerts">
      <div className="flex items-center gap-2">
        <Bell size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Pickup alerts</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        Who&rsquo;s coming, gently.
      </h2>
      <ol
        data-testid="workspace-pickup-alerts-list"
        className="mt-6 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {loading && (
          <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
            A quiet moment…
          </li>
        )}
        {!loading && items.length === 0 && (
          <li
            data-testid="workspace-pickup-alerts-empty"
            className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]"
          >
            No active arrivals. The room is calm.
          </li>
        )}
        {items.map((p) => (
          <li
            key={p.id}
            data-testid={`workspace-pickup-alert-${p.id}`}
            className="bg-[#FBF9F6] p-5 flex items-start gap-4 flex-wrap"
          >
            <span
              className="mt-1 h-9 w-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: p.status === "arrived" ? "#1A1B2E" : "#D4A85F" }}
            >
              <Bell size={16} strokeWidth={1.5} className="text-white" />
            </span>
            <div className="flex-1 min-w-[180px]">
              <div className="font-serif text-lg text-[#2C2B29]">
                {p.parent_name} for {p.child_name}{" "}
                <em className="italic text-[#8A9984]">
                  {p.status === "arrived"
                    ? "is here."
                    : `~${p.eta_minutes ?? "a few"} min away.`}
                </em>
              </div>
              {p.note && (
                <div className="mt-1 text-sm text-[#6A6863] font-light italic">
                  {p.note}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => resolve(p.id)}
              data-testid={`workspace-pickup-resolve-${p.id}`}
              className="btn-ghost !py-1.5 !px-3 !text-[10px]"
            >
              Acknowledge
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Events admin — create, list, and remove. */
function EventsAdmin({ workspaceId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    body: "",
    kind: "info",
    starts_at: "",
    location: "",
    rsvp_required: false,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/events`);
      setEvents(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.starts_at) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/events`, form);
      setForm({
        title: "",
        body: "",
        kind: "info",
        starts_at: "",
        location: "",
        rsvp_required: false,
      });
      await load();
      toast("Event shared gently.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't share.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      await load();
      toast("Removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <section data-testid="workspace-events">
      <div className="flex items-center gap-2">
        <CalendarDays size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Events</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        What&rsquo;s ahead for the families.
      </h2>

      <ol
        data-testid="workspace-events-list"
        className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {loading && (
          <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
            A quiet moment…
          </li>
        )}
        {!loading && events.length === 0 && (
          <li
            data-testid="workspace-events-empty"
            className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]"
          >
            Nothing scheduled. Add the first one below.
          </li>
        )}
        {events.map((ev) => (
          <li
            key={ev.id}
            data-testid={`workspace-event-${ev.id}`}
            className="bg-[#FBF9F6] p-5 flex items-start gap-4"
          >
            <span
              className="mt-1 h-8 w-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#A89CC8" }}
            >
              <CalendarDays size={14} strokeWidth={1.5} className="text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-[#2C2B29]">{ev.title}</div>
              <div className="text-xs text-[#6A6863] mt-1">
                {new Date(ev.starts_at).toLocaleString()}
                {ev.location ? ` · ${ev.location}` : ""}
                {ev.rsvp_required ? " · RSVP" : ""}
              </div>
              {ev.rsvp_required && (ev.rsvp_going > 0 || ev.rsvp_maybe > 0) && (
                <div className="text-xs text-[#6A6863] mt-1">
                  {ev.rsvp_going} going · {ev.rsvp_maybe} maybe
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(ev.id)}
              data-testid={`workspace-event-remove-${ev.id}`}
              aria-label="Remove"
              className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ol>

      <form
        onSubmit={create}
        data-testid="workspace-event-form"
        className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 space-y-5"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow">Title</label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Spring picnic"
              data-testid="workspace-event-title"
              className="orbit-input mt-2"
            />
          </div>
          <div>
            <label className="eyebrow">Kind</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="workspace-event-kind"
              className="orbit-input mt-2 bg-transparent"
            >
              <option value="info">Note</option>
              <option value="field_trip">Field trip</option>
              <option value="parent_meeting">Parent meeting</option>
              <option value="holiday">Holiday</option>
              <option value="closure">Closure</option>
              <option value="celebration">Celebration</option>
            </select>
          </div>
          <div>
            <label className="eyebrow">When</label>
            <input
              type="datetime-local"
              required
              value={form.starts_at}
              onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              data-testid="workspace-event-starts-at"
              className="orbit-input mt-2"
            />
          </div>
          <div>
            <label className="eyebrow">Location (optional)</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Maple Park"
              data-testid="workspace-event-location"
              className="orbit-input mt-2"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="eyebrow">Description (optional)</label>
            <textarea
              rows={2}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="What to bring, a soft note for families…"
              data-testid="workspace-event-body"
              className="orbit-input mt-2 resize-none"
            />
          </div>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm text-[#6A6863]">
            <input
              type="checkbox"
              checked={form.rsvp_required}
              onChange={(e) =>
                setForm({ ...form, rsvp_required: e.target.checked })
              }
              data-testid="workspace-event-rsvp-required"
              className="h-4 w-4 accent-[#1A1B2E]"
            />
            RSVP required (parents will see Going / Maybe / Can&rsquo;t make it)
          </label>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy || !form.title || !form.starts_at}
            data-testid="workspace-event-submit"
            className="btn-primary !py-2 !px-5 !text-xs"
          >
            {busy ? "Sharing…" : "Add event"}
          </button>
        </div>
      </form>
    </section>
  );
}
